/**
 * RPM (Remote Patient Monitoring) Billing Type Definitions
 *
 * RPM is an overlay on existing program enrollments that adds:
 * - Time tracking for staff activities
 * - Reading day tracking from devices
 * - Consent management
 * - CPT code billing eligibility
 */

// =============================================================================
// Enums and Status Types
// =============================================================================

export type RPMStatus = 'active' | 'inactive' | 'pending';
export type ConsentStatus = 'pending' | 'obtained' | 'declined';
export type ConsentMethod = 'verbal' | 'written' | 'electronic';
export type RPMActivityType = 'data_review' | 'call' | 'care_coordination' | 'documentation' | 'other';
export type BillingPeriodStatus = 'in_progress' | 'ready' | 'submitted' | 'billed';

// =============================================================================
// Core Interfaces
// =============================================================================

/**
 * RPM Enrollment - Links to an existing program enrollment
 */
export interface RPMEnrollment {
  id: number;
  patient_id: number;
  enrollment_id: number; // FK to existing program enrollment
  program_name?: string; // Denormalized from enrollment for display
  status: RPMStatus;
  enrolled_at: number; // Unix timestamp
  disenrolled_at?: number;

  // Consent tracking
  consent_status: ConsentStatus;
  consent_date?: number;
  consent_method?: ConsentMethod;
  consent_obtained_by?: string;
  consent_notes?: string;

  // Billing
  billing_provider_id?: number;
  billing_npi?: string;

  // Audit
  created_on: number;
  modified_on: number;
}

/**
 * Time Entry - Staff time spent on RPM activities
 */
export interface RPMTimeEntry {
  id: number;
  rpm_enrollment_id: number;
  patient_id: number;
  staff_id: string;
  staff_name?: string;
  staff_role?: 'provider' | 'navigator'; // For billing distinction

  activity_date: string; // YYYY-MM-DD
  duration_seconds: number; // Actual tracked time in seconds
  duration_minutes?: number; // Deprecated - computed from seconds at billing time
  billing_month: string; // YYYY-MM-01

  activity_type: RPMActivityType;
  is_auto_tracked: boolean;
  notes?: string;

  created_on: number;
}

/**
 * Reading Day - Day with device readings
 */
export interface RPMReadingDay {
  id: number;
  rpm_enrollment_id: number;
  patient_id: number;
  reading_date: string; // YYYY-MM-DD
  billing_month: string; // YYYY-MM-01

  reading_source?: string;
  has_bp: boolean;
  has_weight: boolean;
  has_glucose: boolean;
  has_spo2: boolean;
}

/**
 * Billing Period - Monthly aggregation for billing
 */
export interface RPMBillingPeriod {
  id: number;
  rpm_enrollment_id: number;
  patient_id: number;
  billing_month: string; // YYYY-MM-01

  // Aggregated metrics
  total_reading_days: number;
  total_time_minutes: number;

  // CPT code eligibility
  cpt_99454_eligible: boolean; // 16+ days
  cpt_99445_eligible: boolean; // 2-15 days (new 2026)
  cpt_99457_eligible: boolean; // 20+ min
  cpt_99458_count: number; // Each additional 20 min
  cpt_99470_eligible: boolean; // 10-19 min (new 2026)

  status: BillingPeriodStatus;
  billed_at?: number;
}

// =============================================================================
// Summary and Dashboard Types
// =============================================================================

/**
 * Time summary for a billing month
 */
export interface RPMTimeSummary {
  billing_month: string;
  total_seconds: number; // Exact total in seconds
  total_minutes: number; // Floor of total_seconds / 60 for billing
  by_activity_type: Record<RPMActivityType, number>; // In seconds
  by_staff: Record<string, number>; // In seconds
  by_role: {
    provider_seconds: number;
    navigator_seconds: number;
  };
  billing_tier: BillingTier;
}

/**
 * Reading summary for a billing month
 */
export interface RPMReadingSummary {
  billing_month: string;
  total_reading_days: number;
  days_with_bp: number;
  days_with_weight: number;
  days_remaining_in_month: number;
  days_needed_for_99454: number;
  days_needed_for_99445: number;
  billing_tier: BillingTier;
}

/**
 * Billing tier information
 */
export interface BillingTier {
  codes: string[];
  code?: string | null;
  description: string;
  minutes_to_next_tier?: number;
  days_to_next_tier?: number;
}

/**
 * Dashboard data
 */
export interface RPMDashboardData {
  billing_month: string;
  total_enrolled: number;
  by_program: Record<string, number>;
  patients_with_readings: number;
  patients_near_16_days: number;
  patients_needing_time: number;
}

// =============================================================================
// Request/Response Types
// =============================================================================

export interface CreateRPMEnrollmentPayload {
  enrollment_id: number;
  consent_status?: ConsentStatus;
  consent_method?: ConsentMethod;
  consent_notes?: string;
  billing_provider_id?: number;
  billing_npi?: string;
}

export interface UpdateRPMEnrollmentPayload {
  status?: RPMStatus;
  consent_status?: ConsentStatus;
  consent_method?: ConsentMethod;
  consent_notes?: string;
  billing_provider_id?: number;
  billing_npi?: string;
}

export interface RecordConsentPayload {
  consent_status: 'obtained' | 'declined';
  consent_method?: ConsentMethod;
  consent_notes?: string;
}

export interface AddTimeEntryPayload {
  rpm_enrollment_id: number;
  activity_date: string; // YYYY-MM-DD
  duration_minutes: number;
  activity_type?: RPMActivityType;
  notes?: string;
  is_auto_tracked?: boolean;
}

export interface UpdateTimeEntryPayload {
  duration_minutes?: number;
  activity_type?: RPMActivityType;
  notes?: string;
}

// =============================================================================
// Display Constants
// =============================================================================

export const RPM_STATUS_DISPLAY: Record<RPMStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
};

export const CONSENT_STATUS_DISPLAY: Record<ConsentStatus, string> = {
  pending: 'Pending',
  obtained: 'Obtained',
  declined: 'Declined',
};

export const CONSENT_METHOD_DISPLAY: Record<ConsentMethod, string> = {
  verbal: 'Verbal',
  written: 'Written',
  electronic: 'Electronic',
};

export const RPM_ACTIVITY_DISPLAY: Record<RPMActivityType, string> = {
  data_review: 'Data Review',
  call: 'Patient Call',
  care_coordination: 'Care Coordination',
  documentation: 'Documentation',
  other: 'Other',
};

// Staff role for billing distinction
export type RPMStaffRole = 'provider' | 'navigator';

export const RPM_STAFF_ROLE_DISPLAY: Record<RPMStaffRole, string> = {
  provider: 'Provider',
  navigator: 'Navigator/Staff',
};

export const BILLING_STATUS_DISPLAY: Record<BillingPeriodStatus, string> = {
  in_progress: 'In Progress',
  ready: 'Ready to Bill',
  submitted: 'Submitted',
  billed: 'Billed',
};

// CPT code descriptions
export const CPT_CODE_INFO: Record<string, { description: string; requirement: string }> = {
  '99453': {
    description: 'RPM Initial Setup',
    requirement: 'One-time setup and patient education',
  },
  '99454': {
    description: 'RPM Device Supply',
    requirement: '16+ days of data transmission per month',
  },
  '99445': {
    description: 'RPM Device Supply (Reduced)',
    requirement: '2-15 days of data transmission (New 2026)',
  },
  '99457': {
    description: 'RPM Treatment Management',
    requirement: '20+ minutes of clinical staff time',
  },
  '99458': {
    description: 'RPM Additional Time',
    requirement: 'Each additional 20 minutes beyond 99457',
  },
  '99470': {
    description: 'RPM Treatment Management (Reduced)',
    requirement: '10-19 minutes of clinical staff time (New 2026)',
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate time-based billing tier
 */
export function getTimeBillingTier(minutes: number): BillingTier {
  if (minutes >= 20) {
    const additional20 = Math.floor((minutes - 20) / 20);
    const codes = ['99457'];
    for (let i = 0; i < additional20; i++) {
      codes.push('99458');
    }
    return {
      codes,
      description: additional20 > 0 ? `99457 + ${additional20}x 99458` : '99457 (20+ min)',
      minutes_to_next_tier: minutes % 20 === 0 ? 20 : 20 - (minutes % 20),
    };
  }
  if (minutes >= 10) {
    return {
      codes: ['99470'],
      description: '99470 (10-19 min) - New 2026',
      minutes_to_next_tier: 20 - minutes,
    };
  }
  return {
    codes: [],
    description: 'Not billable (<10 min)',
    minutes_to_next_tier: 10 - minutes,
  };
}

/**
 * Calculate reading-based billing tier
 */
export function getReadingBillingTier(days: number): BillingTier {
  if (days >= 16) {
    return {
      code: '99454',
      codes: ['99454'],
      description: '99454 (16+ days)',
      days_to_next_tier: 0,
    };
  }
  if (days >= 2) {
    return {
      code: '99445',
      codes: ['99445'],
      description: '99445 (2-15 days) - New 2026',
      days_to_next_tier: 16 - days,
    };
  }
  return {
    code: null,
    codes: [],
    description: 'Not billable (<2 days)',
    days_to_next_tier: 2 - days,
  };
}

/**
 * Get all eligible CPT codes for a billing period
 */
export function getEligibleCPTCodes(period: RPMBillingPeriod): string[] {
  const codes: string[] = [];

  // Reading-based codes
  if (period.cpt_99454_eligible) {
    codes.push('99454');
  } else if (period.cpt_99445_eligible) {
    codes.push('99445');
  }

  // Time-based codes
  if (period.cpt_99457_eligible) {
    codes.push('99457');
    for (let i = 0; i < period.cpt_99458_count; i++) {
      codes.push('99458');
    }
  } else if (period.cpt_99470_eligible) {
    codes.push('99470');
  }

  return codes;
}

/**
 * Format minutes as hours and minutes
 */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format seconds as mm:ss or hh:mm:ss
 */
export function formatSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format seconds as detailed time string (e.g., "5 min 30 sec" or "1h 5m 30s")
 */
export function formatSecondsDetailed(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m ${secs}s`;
  }
  if (mins > 0) {
    return secs > 0 ? `${mins} min ${secs} sec` : `${mins} min`;
  }
  return `${secs} sec`;
}

/**
 * Check if consent is valid for billing
 */
export function isConsentValid(enrollment: RPMEnrollment): boolean {
  return enrollment.consent_status === 'obtained';
}

/**
 * Get consent status color for UI
 */
export function getConsentStatusColor(status: ConsentStatus): 'success' | 'warning' | 'error' {
  switch (status) {
    case 'obtained':
      return 'success';
    case 'pending':
      return 'warning';
    case 'declined':
      return 'error';
  }
}

/**
 * Get RPM status color for UI
 */
export function getRPMStatusColor(status: RPMStatus): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'inactive':
      return 'default';
  }
}
